// @ts-check
import { DollarSign, Eye, Globe, Package, Truck, RefreshCw, Search, ShoppingBag, TrendingUp, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import LoadingScreen from '../components/LoadingScreen';
import api from '../utils/apiClient';

const AnalyticsDashboardPage = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [eventCounts, setEventCounts] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [popularSearches, setPopularSearches] = useState([]);
  const [conversionFunnel, setConversionFunnel] = useState(null);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [geographic, setGeographic] = useState([]);
  const [customerMetrics, setCustomerMetrics] = useState(null);
  const [shippingSummary, setShippingSummary] = useState(null);

  const getDays = (range) => (range === '7d' ? 7 : range === '30d' ? 30 : 90);

  const loadAnalytics = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const startDate = getStartDate(timeRange);
      const days = getDays(timeRange);

      try {
        const [events, revenue, products, searches, funnel, daily, geo, customers, shipping] = await Promise.all([
          api.get(`/api/analytics/events/count?start_date=${startDate}`).catch(() => []),
          api.get(`/api/analytics/revenue/summary?start_date=${startDate}`).catch(() => null),
          api
            .get(`/api/analytics/products/top?event_type=add_to_cart&limit=10&start_date=${startDate}`)
            .catch(() => []),
          api.get(`/api/analytics/searches/popular?limit=10&start_date=${startDate}`).catch(() => []),
          api.get(`/api/analytics/conversion-funnel?start_date=${startDate}`).catch(() => null),
          api.get(`/api/analytics/revenue/daily?days=${days}`).catch(() => []),
          api.get('/api/analytics/geographic').catch(() => []),
          api.get(`/api/analytics/customers/metrics?days=${days}`).catch(() => null),
          api.get(`/api/analytics/shipping/summary?start_date=${startDate}`).catch(() => null),
        ]);

        // A 204 response (e.g. a browser extension stubbing out a request that looks like
        // a tracking call) resolves the promise with `null` rather than rejecting it, so
        // `.catch()` above can't fall back for it — coerce here too, or a single blocked
        // call crashes the whole dashboard instead of just showing that one card empty.
        setEventCounts(events || []);
        setRevenueSummary(revenue || null);
        setTopProducts(products || []);
        setPopularSearches(searches || []);
        setConversionFunnel(funnel || null);
        setDailyRevenue(daily || []);
        setGeographic(geo || []);
        setCustomerMetrics(customers || null);
        setShippingSummary(shipping || null);
      } catch (error) {
        console.error('Failed to load analytics:', error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [timeRange],
  );

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const getStartDate = (range) => {
    const date = new Date();
    if (range === '7d') date.setDate(date.getDate() - 7);
    else if (range === '30d') date.setDate(date.getDate() - 30);
    else if (range === '90d') date.setDate(date.getDate() - 90);
    return date.toISOString();
  };

  const getEventCount = (eventType) => {
    const event = eventCounts.find((e) => e.event_type === eventType);
    return event ? event.count : 0;
  };

  const formatINR = (value) => '\u20B9' + Number(value || 0).toLocaleString('en-IN');

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2">Analytics Dashboard</h1>
          <p className="text-muted-foreground">Track performance and customer behavior</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadAnalytics(true)}
            disabled={refreshing}
            className="p-2 border rounded-lg hover:bg-muted/50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <div className="flex gap-1 bg-muted rounded-lg p-1 overflow-x-auto">
            {['7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-card shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card p-5 rounded-lg shadow-sm border">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <span className="text-muted-foreground text-xs font-medium uppercase">Revenue</span>
                <DollarSign className="w-4 h-4 text-green-500" />
              </div>
              <div className="text-2xl font-bold">{formatINR(revenueSummary?.total_revenue)}</div>
              <div className="text-xs text-muted-foreground mt-1">{revenueSummary?.order_count || 0} orders</div>
            </div>

            <div className="bg-card p-5 rounded-lg shadow-sm border">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <span className="text-muted-foreground text-xs font-medium uppercase">Avg Order Value</span>
                <ShoppingBag className="w-4 h-4 text-blue-500" />
              </div>
              <div className="text-2xl font-bold">
                {formatINR(customerMetrics?.average_order_value || revenueSummary?.average_order_value)}
              </div>
            </div>

            <div className="bg-card p-5 rounded-lg shadow-sm border">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <span className="text-muted-foreground text-xs font-medium uppercase">Page Views</span>
                <Eye className="w-4 h-4 text-purple-500" />
              </div>
              <div className="text-2xl font-bold">{getEventCount('page_view').toLocaleString()}</div>
            </div>

            <div className="bg-card p-5 rounded-lg shadow-sm border">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <span className="text-muted-foreground text-xs font-medium uppercase">Conversion Rate</span>
                <TrendingUp className="w-4 h-4 text-orange-500" />
              </div>
              <div className="text-2xl font-bold">
                {conversionFunnel?.conversion_rates?.overall_conversion?.toFixed(2) || '0.00'}%
              </div>
            </div>
          </div>

          {/* Shipping spend */}
          <div className="bg-card p-5 rounded-lg shadow-sm border mb-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <span className="text-muted-foreground text-xs font-medium uppercase">Shipping spend</span>
              <Truck className="w-4 h-4 text-teal-500" />
            </div>
            <div className="text-2xl font-bold">{formatINR(shippingSummary?.total)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {shippingSummary?.shipments || 0} shipments (cancelled ones left out)
            </div>
            {Number(shippingSummary?.total) > 0 && (
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                <span>Freight {formatINR(shippingSummary?.freight)}</span>
                <span>COD fees {formatINR(shippingSummary?.cod)}</span>
                <span>WhatsApp &amp; other {formatINR(shippingSummary?.other)}</span>
              </div>
            )}
          </div>

          {/* Revenue Chart */}
          {dailyRevenue.length > 0 && (
            <div className="bg-card p-6 rounded-lg shadow-sm border mb-6">
              <h2 className="text-lg font-bold mb-4">Revenue Trend</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    stroke="hsl(var(--border))"
                    tickFormatter={(d) => {
                      const date = new Date(d);
                      return `${date.getDate()}/${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    stroke="hsl(var(--border))"
                    tickFormatter={(v) => `\u20B9${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      color: 'hsl(var(--foreground))',
                    }}
                    formatter={(value) => [formatINR(value), 'Revenue']}
                    labelFormatter={(d) =>
                      new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                    }
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Conversion Funnel */}
          {conversionFunnel && (
            <div className="bg-card p-6 rounded-lg shadow-sm border mb-6">
              <h2 className="text-lg font-bold mb-4">Conversion Funnel</h2>
              <div className="space-y-3">
                {[
                  { label: 'Page Views', key: 'page_view', color: 'bg-blue-500', width: '100%' },
                  { label: 'Product Views', key: 'view_content', color: 'bg-indigo-500', rate: 'view_to_cart' },
                  { label: 'Add to Cart', key: 'add_to_cart', color: 'bg-green-500', rate: 'view_to_cart' },
                  {
                    label: 'Checkout Started',
                    key: 'checkout_started',
                    color: 'bg-yellow-500',
                    rate: 'cart_to_checkout',
                  },
                  {
                    label: 'Orders Completed',
                    key: 'order_completed',
                    color: 'bg-purple-500',
                    rate: 'checkout_to_order',
                  },
                ].map((step) => {
                  const count = conversionFunnel.funnel[step.key] || 0;
                  const pageViews = conversionFunnel.funnel.page_view || 1;
                  const width = step.width || `${Math.max((count / pageViews) * 100, 2)}%`;
                  return (
                    <div key={step.key}>
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                        <span className="text-sm font-medium">{step.label}</span>
                        <span className="text-sm font-bold">{count.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-7">
                        <div
                          className={`${step.color} h-7 rounded-full flex items-center justify-end pr-3 transition-all`}
                          style={{ width, minWidth: '40px' }}
                        >
                          <span className="text-white text-xs font-medium">
                            {((count / pageViews) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="pt-3 border-t flex items-center justify-between">
                  <span className="text-sm font-bold">Overall Conversion Rate</span>
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {conversionFunnel.conversion_rates.overall_conversion.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Three Column: Customers, Geographic, Top Products */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Customer Metrics */}
            <div className="bg-card p-6 rounded-lg shadow-sm border">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg font-bold">Customers</h2>
              </div>
              {customerMetrics ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">New Customers</span>
                    <span className="text-lg font-bold">{customerMetrics.new_customers}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">Total Ordering</span>
                    <span className="text-lg font-bold">{customerMetrics.total_ordering_customers}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">Repeat Customers</span>
                    <span className="text-lg font-bold">{customerMetrics.repeat_customers}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950/50 rounded-lg">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Repeat Rate</span>
                    <span className="text-lg font-bold text-blue-700 dark:text-blue-300">
                      {customerMetrics.repeat_rate}%
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No data</p>
              )}
            </div>

            {/* Geographic */}
            <div className="bg-card p-6 rounded-lg shadow-sm border">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-teal-500" />
                <h2 className="text-lg font-bold">By Country</h2>
              </div>
              {geographic.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No data</p>
              ) : (
                <div className="space-y-3">
                  {geographic.slice(0, 8).map((row, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <div className="text-sm font-medium">{row.country}</div>
                        <div className="text-xs text-muted-foreground">{row.order_count} orders</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold">{formatINR(row.revenue)}</div>
                        <div className="text-xs text-muted-foreground">{row.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Products */}
            <div className="bg-card p-6 rounded-lg shadow-sm border">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-orange-500" />
                <h2 className="text-lg font-bold">Top Products</h2>
              </div>
              {topProducts.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No data</p>
              ) : (
                <div className="space-y-3">
                  {topProducts.slice(0, 8).map((product, index) => (
                    <div
                      key={product.product_id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-muted-foreground/70 w-5">#{index + 1}</span>
                        <div>
                          <div className="text-sm font-medium">
                            {product.product_name || product.product_id.substring(0, 8) + '...'}
                          </div>
                          <div className="text-xs text-muted-foreground">{product.event_count} cart adds</div>
                        </div>
                      </div>
                      {product.revenue > 0 && (
                        <div className="text-sm font-bold text-green-600 dark:text-green-400">
                          {formatINR(product.revenue)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Two Column: Searches + Event Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Popular Searches */}
            <div className="bg-card p-6 rounded-lg shadow-sm border">
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-bold">Popular Searches</h2>
              </div>
              {popularSearches.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No search data yet</p>
              ) : (
                <div className="space-y-3">
                  {popularSearches.map((search, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-muted-foreground/70 w-5">#{index + 1}</span>
                        <span className="text-sm font-medium">{search.search_query}</span>
                      </div>
                      <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                        {search.count} searches
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Event Summary */}
            <div className="bg-card p-6 rounded-lg shadow-sm border">
              <h2 className="text-lg font-bold mb-4">All Events</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {eventCounts.map((event) => (
                  <div key={event.event_type} className="p-3 bg-muted/50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1 uppercase">
                      {event.event_type.replaceAll('_', ' ')}
                    </div>
                    <div className="text-xl font-bold">{event.count.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboardPage;
